import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { APP_ICONS } from "../../assets";
import MUI_THEME from "../../theme";
import AllChats from "./AllChats";
import { getDatabase, ref, onValue, off, update } from "firebase/database";

const style = {
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#F3F3F3",
      "& .MuiOutlinedInput-notchedOutline": {
        border: `none`,
        boxShadow: "none",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `none`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `none`,
      },
    },
  },
};

function AllUsers({ businessList, usersList, searchVall, setSearchVall }) {
  const { user } = useSelector((state) => state.user);
  const [tabValue, setTabValue] = useState("worker");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState({ worker: 0, customer: 0 });

  const arrayToMap = tabValue === "worker" ? businessList : usersList;

  useEffect(() => {
    const db = getDatabase();
    const listeners = [];

    const fetchUnreadCounts = () => {
      let newUnreadCounts = {};
      let workerUnread = 0;
      let customerUnread = 0;

      const targetList = tabValue === "worker" ? businessList : usersList;

      targetList.forEach((chatUser) => {
        const chatRef = ref(db, `messages/${user._id}/${chatUser._id}`);

        const listener = onValue(chatRef, (snapshot) => {
          const messages = snapshot.val();
          let count = 0;
          if (messages) {
            Object.values(messages).forEach((msg) => {
              if (!msg.read && msg.sender === chatUser._id) {
                count++;
              }
            });
          }

          newUnreadCounts[chatUser._id] = count;

          if (tabValue === "worker") {
            workerUnread += count;
          } else {
            customerUnread += count;
          }

          // Set per-user unread count
          setUnreadCounts((prev) => ({
            ...prev,
            [chatUser._id]: count,
          }));

          // Set total unread count for each tab
          setTotalUnread((prev) => ({
            ...prev,
            worker: workerUnread,
            customer: customerUnread,
          }));
        });

        listeners.push({ ref: chatRef, listener });
      });
    };

    fetchUnreadCounts();

    return () => {
      listeners.forEach(({ ref: dbRef, listener }) =>
        off(dbRef, "value", listener)
      );
    };
  }, [tabValue, businessList, usersList, user._id]);

  const markMessagesAsRead = async (otherUserId) => {
    const db = getDatabase();
    const msgRef = ref(db, `messages/${user._id}/${otherUserId}`);

    onValue(
      msgRef,
      (snapshot) => {
        const data = snapshot.val();
        if (!data) return;

        const updates = {};
        Object.entries(data).forEach(([msgId, msg]) => {
          if (!msg.read && msg.sender === otherUserId) {
            updates[`messages/${user._id}/${otherUserId}/${msgId}/read`] = true;
          }
        });

        if (Object.keys(updates).length > 0) {
          update(ref(db), updates);
        }

        // locally clear unread count
        setUnreadCounts((prev) => ({
          ...prev,
          [otherUserId]: 0,
        }));

        // decrement total
        setTotalUnread((prev) => {
          const tabKey = tabValue === "worker" ? "worker" : "customer";
          return {
            ...prev,
            [tabKey]: Math.max(
              0,
              (prev[tabKey] || 0) - (unreadCounts[otherUserId] || 0)
            ),
          };
        });
      },
      { onlyOnce: true }
    );
  };

  return (
    <Paper sx={{ p: 2, borderRadius: "10px" }}>
      <Stack gap={1.5} direction={"row"} alignItems={"center"}>
        <img src={APP_ICONS.handshakeIcon} alt="handshake-icon" width={30} />
        <Stack direction={"row"} alignItems={"center"}>
          <Typography variant="h1" fontSize={"1.125rem"} fontWeight={"bold"}>
            Messages
          </Typography>
          <ExpandMoreIcon />
        </Stack>
      </Stack>

      <Tabs
        value={tabValue}
        onChange={(e, val) => {
          setTabValue(val);
          setSearchVall("");
        }}
        textColor="secondary"
        indicatorColor="secondary"
        aria-label="secondary tabs"
      >
        <Tab
          sx={{ fontSize: 10 }}
          value="worker"
          label={`ALL WORKERS (${totalUnread.worker})`}
        />
        <Tab
          sx={{ fontSize: 10 }}
          value="customer"
          label={`ALL CUSTOMERS (${totalUnread.customer})`}
        />
      </Tabs>

      <Box my={3}>
        <TextField
          variant="outlined"
          sx={style.searchField}
          fullWidth
          placeholder="Search message"
          value={searchVall}
          onChange={(e) => setSearchVall(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Stack
        p={1}
        gap={1.5}
        height={"50vh"}
        overflow={"auto"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: MUI_THEME.palette.primary,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: MUI_THEME.palette.secondary,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: MUI_THEME.palette.lightGrey,
          },
        }}
      >
        {arrayToMap?.map((item) => (
          <AllChats
            key={item._id}
            userDetail={item}
            tabValue={tabValue}
            unreadCount={unreadCounts[item._id] || 0}
            onSelect={markMessagesAsRead}
          />
        ))}
      </Stack>
    </Paper>
  );
}

export default AllUsers;
